'use client'

import type { ReactNode } from 'react'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { CarouselApi } from '@ui/web/components'
import { Autoplay, Carousel, CarouselContent } from '@ui/web/components'
import { BookRowLeftChevronIcon, BookRowRightChevronIcon } from '@ui/web/icon'
import cn from '@config/tailwind/cn'

type ContentCarouselAlign = 'start' | 'center'
type ContentCarouselArrowSize = 'default' | 'small'

const arrowSizeMap: Record<ContentCarouselArrowSize, number> = {
  default: 39,
  small: 24,
}

interface ContentCarouselProps {
  autoplay?: boolean
  loop?: boolean
  align?: ContentCarouselAlign
  arrowSize?: ContentCarouselArrowSize
  children?: ReactNode
  className?: string
}

export function ContentCarousel({
  autoplay = false,
  loop = false,
  align = 'start',
  arrowSize = 'default',
  children,
  className,
}: ContentCarouselProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(true)
  const [isTouch, setIsTouch] = useState(false)
  const plugin = useRef(Autoplay({ delay: 4000, stopOnInteraction: true }))
  const iconSize = arrowSizeMap[arrowSize]

  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  useEffect(() => {
    if (!api) return

    const update = () => {
      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }

    update()
    api.on('select', update)
    api.on('reInit', update)

    return () => {
      api.off('select', update)
      api.off('reInit', update)
    }
  }, [api])

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api])
  const scrollNext = useCallback(() => api?.scrollNext(), [api])

  const prevDisabled = !loop && !canScrollPrev
  const nextDisabled = !loop && !canScrollNext

  return (
    <div className={cn('srd:w-full srd:mx-auto srd:relative', className)}>
      <Carousel
        plugins={autoplay ? [plugin.current] : []}
        setApi={setApi}
        className='srd:w-full'
        opts={{ align, loop }}
      >
        {/* py-3 pl-4 pr-4: gives room on all sides for the scale hover animation to not get clipped */}
        <CarouselContent className='srd:pl-1 srd:py-2'>{children}</CarouselContent>

        {!isTouch && (
          <>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  'srd:absolute srd:top-1/2 srd:left-8 srd:-translate-y-1/2 srd:-translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:transition-opacity',
                  prevDisabled ? 'srd:cursor-not-allowed' : 'srd:cursor-pointer',
                )}
                onClick={prevDisabled ? undefined : scrollPrev}
              >
                <BookRowLeftChevronIcon size={iconSize} className={cn(prevDisabled && 'srd:opacity-40')} />
                <span className='srd:sr-only'>Sebelumnya</span>
              </motion.div>
            </AnimatePresence>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  'srd:absolute srd:top-1/2 srd:right-8 srd:-translate-y-1/2 srd:translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:transition-opacity',
                  nextDisabled ? 'srd:cursor-not-allowed' : 'srd:cursor-pointer',
                )}
                onClick={nextDisabled ? undefined : scrollNext}
              >
                <BookRowRightChevronIcon size={iconSize} className={cn(nextDisabled && 'srd:opacity-40')} />
                <span className='srd:sr-only'>Selanjutnya</span>
              </motion.div>
            </AnimatePresence>
          </>
        )}
      </Carousel>
    </div>
  )
}
